/**
 * ADS abbreviation expansions — bootstrap seed / offline fallback.
 *
 * The source of truth is now the DynamoDB-backed `/abbreviations` list, managed
 * via the in-app admin panel (MXO-L6). At runtime the abbreviationStore seeds
 * from the values below so note expansion works on first paint and offline, then
 * refreshes from the API after login (see abbreviationStore + refreshAbbreviationStore).
 *
 * This list only needs updating to change the initial seed. It also feeds the
 * one-time DynamoDB seed (backend/beta-api/scripts/abbreviations-seed.json).
 */

/** A single abbreviation and the text it expands to. */
export interface AbbreviationEntry {
  abbreviation: string;
  expansion: string;
}

/**
 * The main abbreviation set. Matched as whole, case-sensitive (uppercase)
 * tokens; the expander applies longer abbreviations before shorter ones.
 */
export const ADS_ABBREVIATIONS: readonly AbbreviationEntry[] = [
  { abbreviation: 'A/C', expansion: 'aircraft' },
  { abbreviation: 'T/S', expansion: 'troubleshooting' },
  { abbreviation: 'T/R', expansion: 'thrust reverser' },
  { abbreviation: 'P/N', expansion: 'part number' },
  { abbreviation: 'ACM', expansion: 'air cycle machine' },
  { abbreviation: 'ADP', expansion: 'air driven pump' },
  { abbreviation: 'AGB', expansion: 'accessory gearbox' },
  { abbreviation: 'AGL', expansion: 'above ground level' },
  { abbreviation: 'AIL', expansion: 'aileron' },
  { abbreviation: 'APP', expansion: 'approach' },
  { abbreviation: 'APU', expansion: 'auxiliary power unit' },
  { abbreviation: 'ASSY', expansion: 'assembly' },
  { abbreviation: 'ATC', expansion: 'air traffic control' },
  { abbreviation: 'AUTO', expansion: 'automatic' },
  { abbreviation: 'BARO', expansion: 'barometric' },
  { abbreviation: 'BAT', expansion: 'battery' },
  { abbreviation: 'BITE', expansion: 'built-in-test equipment' },
  { abbreviation: 'BIT', expansion: 'built-in-test' },
  { abbreviation: 'CCW', expansion: 'counterclockwise' },
  { abbreviation: 'CKPT', expansion: 'cockpit' },
  { abbreviation: 'CONFIG', expansion: 'configuration' },
  { abbreviation: 'DEG', expansion: 'degree' },
  { abbreviation: 'DEP', expansion: 'departure' },
  { abbreviation: 'DES', expansion: 'descent' },
  { abbreviation: 'DISENG', expansion: 'disengage' },
  { abbreviation: 'DISC', expansion: 'disconnect' },
  { abbreviation: 'ECS', expansion: 'environmental control system' },
  { abbreviation: 'EDP', expansion: 'engine driven pump' },
  { abbreviation: 'EGPWS', expansion: 'enhanced ground proximity warning system' },
  { abbreviation: 'EGT', expansion: 'exhaust gas temperature' },
  { abbreviation: 'EICAS', expansion: 'Engine Indication and Crew Alerting System' },
  { abbreviation: 'ELEV', expansion: 'elevator' },
  { abbreviation: 'ELT', expansion: 'emergency locator transmitter' },
  { abbreviation: 'ENG', expansion: 'engine' },
  { abbreviation: 'EPR', expansion: 'engine pressure ratio' },
  { abbreviation: 'EVAC', expansion: 'evacuation' },
  { abbreviation: 'FADEC', expansion: 'full authority digital engine control' },
  { abbreviation: 'FDR', expansion: 'flight data recorder' },
  { abbreviation: 'FLT', expansion: 'flight' },
  { abbreviation: 'FMC', expansion: 'flight management computer' },
  { abbreviation: 'FMS', expansion: 'flight management system' },
  { abbreviation: 'FOD', expansion: 'foreign object damage' },
  { abbreviation: 'FWD', expansion: 'forward' },
  { abbreviation: 'GALY', expansion: 'galley' },
  { abbreviation: 'GNSS', expansion: 'global navigation satellite system' },
  { abbreviation: 'GPS', expansion: 'global positioning system' },
  { abbreviation: 'GPWS', expansion: 'ground proximity warning system' },
  { abbreviation: 'GRD', expansion: 'ground' },
  { abbreviation: 'HDG', expansion: 'heading' },
  { abbreviation: 'HYD', expansion: 'hydraulic' },
  { abbreviation: 'IDENT', expansion: 'identification' },
  { abbreviation: 'IDG', expansion: 'integrated drive generator' },
  { abbreviation: 'ILS', expansion: 'instrument landing system' },
  { abbreviation: 'KIAS', expansion: 'knots indicated airspeed' },
  { abbreviation: 'LAV', expansion: 'lavatory' },
  { abbreviation: 'LDR', expansion: 'loader' },
  { abbreviation: 'LH', expansion: 'left-hand' },
  { abbreviation: 'LT', expansion: 'left' },
  { abbreviation: 'LNAV', expansion: 'lateral navigation' },
  { abbreviation: 'LOC', expansion: 'localizer' },
  { abbreviation: 'LPT', expansion: 'low pressure turbine' },
  { abbreviation: 'LRU', expansion: 'line replaceable unit' },
  { abbreviation: 'MAG', expansion: 'magnetic' },
  { abbreviation: 'MFD', expansion: 'multi-function display' },
  { abbreviation: 'MLG', expansion: 'main landing gear' },
  { abbreviation: 'MSL', expansion: 'mean sea level' },
  { abbreviation: 'MX', expansion: 'maintenance' },
  { abbreviation: 'NAV', expansion: 'navigation' },
  { abbreviation: 'NLG', expansion: 'nose landing gear' },
  { abbreviation: 'PAX', expansion: 'passenger' },
  { abbreviation: 'PFD', expansion: 'primary flight display' },
  { abbreviation: 'PRV', expansion: 'pressure regulating valve' },
  { abbreviation: 'QTY', expansion: 'quantity' },
  { abbreviation: 'RAT', expansion: 'ram air turbine' },
  { abbreviation: 'RH', expansion: 'right-hand' },
  { abbreviation: 'RT', expansion: 'right' },
  { abbreviation: 'RUD', expansion: 'rudder' },
  { abbreviation: 'RVR', expansion: 'runway visual range' },
  { abbreviation: 'SAV', expansion: 'starter air valve' },
  { abbreviation: 'SOV', expansion: 'shut-off valve' },
  { abbreviation: 'SPD', expansion: 'speed' },
  { abbreviation: 'STAB', expansion: 'stabilizer' },
  { abbreviation: 'TBC', expansion: 'to be confirmed' },
  { abbreviation: 'TBD', expansion: 'to be determined' },
  { abbreviation: 'TCAS', expansion: 'traffic collision avoidance system' },
  { abbreviation: 'WHL', expansion: 'wheel' },
  { abbreviation: 'WRN', expansion: 'warning' },
  { abbreviation: 'WXR', expansion: 'weather radar' },
  { abbreviation: 'WX', expansion: 'weather' },
  { abbreviation: 'XPNDR', expansion: 'transponder' },
  { abbreviation: 'L/E', expansion: 'leading edge' },
  { abbreviation: 'LE', expansion: 'leading edge' },
];

/**
 * Single-letter abbreviations expanded only as standalone tokens, applied after
 * the main set (per the source file) so they never interfere with
 * multi-character abbreviations.
 */
export const ADS_STANDALONE_ABBREVIATIONS: readonly AbbreviationEntry[] = [
  { abbreviation: 'L', expansion: 'left' },
  { abbreviation: 'R', expansion: 'right' },
];
